/**
 * Bridges J-code text-field edits into shared CPT table update logic.
 * @module handleValueChangeOfTextField
 */
import { CptCodesTableRowModel } from "../../../../../data-model/cptCodesTableModel";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { CptCodeWithDetailsKeys } from "../../../../../enums/tableColumnKeys";
import { updateTableDataState } from "./updateTableDataState";

/**
 * Updates a text-input cell for the row matching `id` and marks dirty state
 * via {@link updateTableDataState}.
 *
 * Dispatches on `fieldKey`: handles the J-code column
 * ({@link CptCodeWithDetailsKeys.JCodes}), the authorization-number column
 * ({@link CptCodeWithDetailsKeys.AuthorizationNumber}), and the pending-number
 * column ({@link CptCodeWithDetailsKeys.PendingNumber}). Other keys are ignored.
 *
 * @param value - New cell string from the text field
 * @param id - Row `jCodeId` used to locate the target row
 * @param fieldKey - Table column key identifying which cell changed; one of
 *   {@link CptCodeWithDetailsKeys.JCodes},
 *   {@link CptCodeWithDetailsKeys.AuthorizationNumber}, or
 *   {@link CptCodeWithDetailsKeys.PendingNumber}
 * @param tableData - Current CPT table rows
 * @param originalTableData - Snapshot used for change detection
 * @param setTableData - React state setter for table rows
 * @param setHasChanges - React state setter for unsaved changes
 * @param saveTableDataToContext - Persists rows to session store
 * @param generalOrderDetails - Order context for validation in downstream updates
 * @param authOnFileDetails - Auth-on-file context for validation in downstream updates
 * @param category - Healthcare facility `category` for validation in downstream updates
 */
export const handleValueChangeOfTextField = (
  value: string,
  id: string,
  fieldKey: string,
  tableData: CptCodesTableRowModel[],
  originalTableData: CptCodesTableRowModel[],
  setTableData: React.Dispatch<React.SetStateAction<CptCodesTableRowModel[]>>,
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>,
  saveTableDataToContext: (data: CptCodesTableRowModel[]) => void,
  generalOrderDetails: GeneralOrderDetails | null | undefined,
  authOnFileDetails: AuthOnFile | null | undefined,
  category?: string | null,
) => {
  const findRowIndex = tableData.findIndex((row) => row.jCodeId === id);
  if (findRowIndex !== -1) {
    if (fieldKey === CptCodeWithDetailsKeys.JCodes) {
      updateTableDataState(
        findRowIndex,
        CptCodeWithDetailsKeys.JCodes,
        value,
        generalOrderDetails ?? ({} as GeneralOrderDetails | null | undefined),
        authOnFileDetails ?? ({} as AuthOnFile | null | undefined),
        originalTableData,
        setTableData,
        setHasChanges,
        saveTableDataToContext,
        category,
      );
    } else if (fieldKey === CptCodeWithDetailsKeys.AuthorizationNumber) {
      updateTableDataState(
        findRowIndex,
        CptCodeWithDetailsKeys.AuthorizationNumber,
        value,
        generalOrderDetails ?? ({} as GeneralOrderDetails | null | undefined),
        authOnFileDetails ?? ({} as AuthOnFile | null | undefined),
        originalTableData,
        setTableData,
        setHasChanges,
        saveTableDataToContext,
        category,
      );
    } else if (fieldKey === CptCodeWithDetailsKeys.PendingNumber) {
      updateTableDataState(
        findRowIndex,
        CptCodeWithDetailsKeys.PendingNumber,
        value,
        generalOrderDetails ?? ({} as GeneralOrderDetails | null | undefined),
        authOnFileDetails ?? ({} as AuthOnFile | null | undefined),
        originalTableData,
        setTableData,
        setHasChanges,
        saveTableDataToContext,
        category,
      );
    }
  }
};
